import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, IconSpinner } from '../../../components';
import { getProviderCompletedTransactions } from '../../../util/api';
import css from '../EventProviderDashboardPageForm.module.css';
import { getEventTypeLabel } from '../../../util/data';
import classNames from 'classnames';
import { capitalize } from 'lodash';

const PER_PAGE = 10;

const ProviderCompletedEventsTab = props => {
	const { currentUserId } = props;

	const history = useHistory();
	const [transactions, setTransactions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [meta, setMeta] = useState({ totalItems: 0, totalPages: 1, page: 1, perPage: PER_PAGE });
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		const fetchTransactions = async () => {
			try {
				setLoading(true);
				setError(null);

				const response = await getProviderCompletedTransactions({
					userId: currentUserId,
					page: currentPage,
					perPage: PER_PAGE,
				});

				if (response.error) {
					setError(response.error);
				} else {
					setTransactions(response.data?.transactions || []);
					setMeta(
						response.data?.meta || { totalItems: 0, totalPages: 1, page: currentPage, perPage: PER_PAGE }
					);
				}
			} catch (err) {
				setError('Failed to fetch completed events');
				console.error('Error fetching completed transactions:', err);
			} finally {
				setLoading(false);
			}
		};

		if (currentUserId) {
			fetchTransactions();
		}
	}, [currentUserId, currentPage]);

	const totalPages = typeof meta?.totalPages === 'number' && meta.totalPages > 0 ? meta.totalPages : 1;
	const shouldShowPagination = totalPages > 1;

	const handleViewTransaction = (paymentTransactionId) => {
		history.push(`/sale/${paymentTransactionId}`);
	};

	const formatEventDate = eventDetails => {
		if (!eventDetails || !eventDetails[0]) return 'N/A';
		return eventDetails[0].date || 'N/A';
	};

	const formatEventTime = eventDetails => {
		if (!eventDetails || !eventDetails[0]) return 'N/A';
		return eventDetails[0].photoBoothStartTime || 'N/A';
	};

	const getClientName = item => {
		return (
			item.author?.attributes?.profile?.displayName ||
			item.author?.attributes?.profile?.publicData?.companyName ||
			'-'
		);
	};

	if (loading) {
		return (
			<div className={css.eventDashboardFormContainer}>
				<div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
					<IconSpinner />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={css.eventDashboardFormContainer}>
				<div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
					Error: {error}
				</div>
			</div>
		);
	}

	return (
		<div className={css.eventDashboardFormContainer}>
			<div className={css.eventDashboardFormHeader}>
				<div className={css.eventDashboardFormTitleContainer}>
					<h1 className={css.eventDashboardFormTitle}>Completed Events</h1>
				</div>
			</div>

			<div className={css.cardContainer}>
				{/* Desktop Table View */}
				<div className={css.eventDashboardTableContainer}>
					<table className={css.eventDashboardTable}>
						<thead>
							<tr>
								<th>EVENT DETAILS</th>
								<th>CLIENT</th>
								<th>DATE & TIME</th>
								<th>STATUS</th>
								<th>AMOUNT</th>
								<th>ACTIONS</th>
							</tr>
						</thead>
						<tbody>
							{transactions.length === 0 ? (
								<tr>
									<td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
										No completed events found
									</td>
								</tr>
							) : (
								transactions.map((item, index) => {
									const listingEntity = item?.listing;
									const listingPublicData = listingEntity?.attributes?.publicData || {};
									const eventType = listingPublicData.eventType || '-';
									const eventTypeLabel = getEventTypeLabel(eventType);
									const eventDate = formatEventDate(listingPublicData.eventDetails);
									const eventTime = formatEventTime(listingPublicData.eventDetails);
									const clientName = getClientName(item);
									const bidAmount = item.protectedData?.finalBid?.bidAmount;
									const amount = bidAmount ? `$${(bidAmount / 100).toFixed(0)}` : '$0';

									return (
										<tr key={`${item.transactionId}-${index}`}>
											<td>
												<div className={css.eventDetails}>
													<div className={css.eventTitle}>{listingEntity?.attributes?.title || 'N/A'}</div>
													<div className={css.eventType}>{eventTypeLabel}</div>
													<div className={css.eventId}>{listingEntity?.id?.uuid || 'N/A'}</div>
												</div>
											</td>
											<td>
												<div className={css.client}>{clientName}</div>
											</td>
											<td>
												<div className={css.dateTime}>
													<div className={css.date}>{eventDate}</div>
													<div className={css.time}>{eventTime}</div>
												</div>
											</td>
											<td>
												<span className={classNames(css.statusBadge, css.statusBadgeCompleted)}>
													Event completed
												</span>
											</td>
											<td>
												<div className={css.invoiceAmount}>
													<div className={css.invoiceAmountValue}>{amount}</div>
												</div>
											</td>
											<td>
												<Button
													onClick={() => handleViewTransaction(item.paymentTransactionId)}
													className={`${css.actionButton} ${css.actionButtonView}`}
												>
													View
												</Button>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>

				{/* Mobile Card View */}
				<div className={css.eventDashboardCardsView}>
					{transactions.map((item, index) => {
						const listingEntity = item?.listing;
						const listingPublicData = listingEntity?.attributes?.publicData || {};
						const eventType = listingPublicData.eventType || '-';
						const eventTypeLabel = getEventTypeLabel(eventType);
						const eventDate = formatEventDate(listingPublicData.eventDetails);
						const eventTime = formatEventTime(listingPublicData.eventDetails);
						const clientName = getClientName(item);
						const bidAmount = item.protectedData?.finalBid?.bidAmount;
						const amount = bidAmount ? `$${(bidAmount / 100).toFixed(0)}` : '$0';

						return (
							<div key={`${item.transactionId}-${index}`} className={css.eventCard}>
								<div className={css.eventCardHeader}>
									<div className={css.eventCardTitleSection}>
										<div className={css.eventCardTitle}>{listingEntity?.attributes?.title || 'N/A'}</div>
										<div className={css.eventCardType}>{eventTypeLabel}</div>
										<div className={css.eventCardId}>{listingEntity?.id?.uuid || 'N/A'}</div>
									</div>
									<div className={css.eventCardStatus}>
										<span className={classNames(css.statusBadge, css.statusBadgeCompleted)}>
											Event completed
										</span>
									</div>
								</div>
								<div className={css.eventCardDetails}>
									<div className={css.eventCardDetailRow}>
										<div className={css.eventCardDetailLabel}>CLIENT</div>
										<div className={css.eventCardDetailValue}>{clientName}</div>
									</div>
									<div className={css.eventCardDetailRow}>
										<div className={css.eventCardDetailLabel}>DATE & TIME</div>
										<div>
											<div className={css.eventCardDetailValue}>{eventDate}</div>
											<div className={css.eventCardDetailSubValue}>{eventTime}</div>
										</div>
									</div>
									<div className={css.eventCardDetailRow}>
										<div className={css.eventCardDetailLabel}>AMOUNT</div>
										<div>
											<div className={css.eventCardDetailValue}>{amount}</div>
										</div>
									</div>
								</div>
								<div className={css.eventCardAction}>
									<Button
										onClick={() => handleViewTransaction(item.paymentTransactionId)}
										className={`${css.actionButton} ${css.actionButtonView}`}
									>
										View
									</Button>
								</div>
							</div>
						);
					})}
				</div>

				{/* Pagination */}
				{shouldShowPagination && (
					<div className={css.paginationContainer}>
						<button
							className={css.paginationArrow}
							onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
							disabled={currentPage === 1}
							aria-label="Previous page"
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12.5 15L7.5 10L12.5 5" stroke="#383F52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
						<div className={css.paginationNumbers}>
							{Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
								<span
									key={pageNum}
									className={`${css.paginationNumber} ${pageNum === currentPage ? css.paginationNumberActive : ''}`}
									onClick={() => setCurrentPage(pageNum)}
									style={{ cursor: 'pointer' }}
								>
									{pageNum}
								</span>
							))}
						</div>
						<button
							className={css.paginationArrow}
							onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
							disabled={currentPage === totalPages}
							aria-label="Next page"
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M7.5 5L12.5 10L7.5 15" stroke="#383F52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default ProviderCompletedEventsTab;
